import React, { FC, useEffect, useState } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  TreatmentAgency,
  TreatmentAgencyStatus,
} from "@/domain/treatmentAgencies.interface";
import { TreatmentAgencyPayload } from "@/services/treatmentAgencies.service";

type LocationFormEntry = {
  _id?: string;
  code: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  latitude: number | string;
  longitude: number | string;
};

type TreatmentAgencyFormData = {
  name: string;
  status: TreatmentAgencyStatus;
  locations: LocationFormEntry[];
};

const STATUSES: TreatmentAgencyStatus[] = ["ACTIVE", "INACTIVE"];

const optionalNumber = () =>
  yup
    .number()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return typeof value === "number" && !isNaN(value) ? value : undefined;
    })
    .nullable()
    .optional();

const buildSchema = () =>
  yup.object().shape({
    name: yup.string().trim().required("Name is required"),
    status: yup
      .mixed<TreatmentAgencyStatus>()
      .oneOf(STATUSES)
      .required("Status is required"),
    locations: yup
      .array()
      .of(
        yup.object().shape({
          _id: yup.string().optional(),
          code: yup.string().trim().required("Code is required"),
          email: yup
            .string()
            .trim()
            .email("Invalid email")
            .optional()
            .transform((v) => (v === "" ? undefined : v)),
          phone: yup.string().trim().optional(),
          addressLine1: yup
            .string()
            .trim()
            .required("Address line 1 is required"),
          addressLine2: yup.string().trim().optional(),
          city: yup.string().trim().required("City is required"),
          postalCode: yup.string().trim().required("Postal code is required"),
          latitude: optionalNumber(),
          longitude: optionalNumber(),
        })
      )
      .default([]),
  });

const DEFAULT_VALUES: TreatmentAgencyFormData = {
  name: "",
  status: "ACTIVE",
  locations: [],
};

const coerceNumber = (
  value: number | string | undefined
): number | undefined => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = typeof value === "number" ? value : Number(value);
  return isNaN(n) ? undefined : n;
};

type TreatmentAgencyModalProps = {
  open: boolean;
  onClose: () => void;
  treatmentAgency?: TreatmentAgency;
  onSave: (data: TreatmentAgencyPayload) => void;
  isPending: boolean;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel: FC<TabPanelProps> = ({ children, index, value }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 2 }}>
      {children}
    </Box>
  );
};

const emptyLocation = (): LocationFormEntry => ({
  code: "",
  email: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  latitude: "",
  longitude: "",
});

const TreatmentAgencyModal: FC<TreatmentAgencyModalProps> = ({
  open,
  onClose,
  treatmentAgency,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(treatmentAgency);

  const [activeTab, setActiveTab] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TreatmentAgencyFormData>({
    resolver: yupResolver(buildSchema()) as never,
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({
    control,
    name: "locations",
  });

  useEffect(() => {
    if (!open) return;
    setActiveTab(0);
    if (treatmentAgency) {
      reset({
        name: treatmentAgency.name || "",
        status: (treatmentAgency.status as TreatmentAgencyStatus) || "ACTIVE",
        locations: Array.isArray(treatmentAgency.locations)
          ? treatmentAgency.locations.map((l) => ({
              _id: l._id,
              code: l.code ?? "",
              email: l.email ?? "",
              phone: l.phone ?? "",
              addressLine1: l.addressLine1 ?? "",
              addressLine2: l.addressLine2 ?? "",
              city: l.city ?? "",
              postalCode: l.postalCode ?? "",
              latitude: l.latitude ?? "",
              longitude: l.longitude ?? "",
            }))
          : [],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [treatmentAgency, open, reset]);

  const onSubmit = (data: TreatmentAgencyFormData) => {
    const payload: TreatmentAgencyPayload = {
      name: data.name.trim(),
      status: data.status,
      locations: (data.locations || []).map((l) => {
        const entry: TreatmentAgencyPayload["locations"][number] = {
          code: l.code.trim(),
          addressLine1: l.addressLine1.trim(),
          city: l.city.trim(),
          postalCode: l.postalCode.trim(),
        };
        if (l._id) entry._id = l._id;
        if (l.email?.trim()) entry.email = l.email.trim();
        if (l.phone?.trim()) entry.phone = l.phone.trim();
        if (l.addressLine2?.trim()) entry.addressLine2 = l.addressLine2.trim();
        const lat = coerceNumber(l.latitude);
        const lng = coerceNumber(l.longitude);
        if (lat !== undefined) entry.latitude = lat;
        if (lng !== undefined) entry.longitude = lng;
        return entry;
      }),
    };
    onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit Treatment Agency" : "Create Treatment Agency"}
      size={{
        width: "100%",
        minHeight: 560,
        maxHeight: 820,
        maxWidth: 880,
      }}
      content={
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="General" />
            <Tab label={`Locations (${locationFields.length})`} />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={8}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message || ""}
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Status"
                      fullWidth
                      required
                      error={!!errors.status}
                      helperText={errors.status?.message || ""}
                      disabled={isPending}
                    >
                      {STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="labelLarge" component="div">
                Locations
              </Typography>
              <Button
                variantType="save"
                label="Add location"
                onClick={() => appendLocation(emptyLocation())}
                disabled={isPending}
              />
            </Stack>

            <Stack spacing={2}>
              {locationFields.length === 0 && (
                <Typography variant="bodyMedium" color="text.secondary">
                  No locations added.
                </Typography>
              )}

              {locationFields.map((f, idx) => (
                <Box
                  key={f.id}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: theme.palette.outline.main,
                    borderRadius: "8px",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="titleSmall" component="div">
                      Location #{idx + 1}
                    </Typography>
                    <IconButton
                      aria-label="Remove location"
                      onClick={() => removeLocation(idx)}
                      disabled={isPending}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`locations.${idx}.code`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Code"
                            fullWidth
                            required
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`locations.${idx}.email`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Email"
                            type="email"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`locations.${idx}.phone`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Phone"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Controller
                        name={`locations.${idx}.addressLine1`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Address line 1"
                            fullWidth
                            required
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`locations.${idx}.addressLine2`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Address line 2"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`locations.${idx}.city`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="City"
                            fullWidth
                            required
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`locations.${idx}.postalCode`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            label="Postal code"
                            fullWidth
                            required
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`locations.${idx}.latitude`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            type="number"
                            label="Latitude"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                            inputProps={{ step: "any" }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`locations.${idx}.longitude`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            type="number"
                            label="Longitude"
                            fullWidth
                            disabled={isPending}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || ""}
                            inputProps={{ step: "any" }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </TabPanel>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default TreatmentAgencyModal;
